import { validateEmail } from '@utils';
import React, { useState } from 'react';

interface ContactFormProps {
	onClose: () => void;
	onSubmit: (data: FormData) => void;
	modelref: React.RefObject<HTMLDivElement>;
}

interface FormData {
	email: string;
	message: string;
}


export const ContactForm: React.FC<ContactFormProps> = ({ onClose, modelref, onSubmit }) => {
	const [formData, setFormData] = useState<FormData>({
		email: '',
		message: '',
	});
	const [errors, setErrors] = useState<Partial<FormData>>({});

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));

		// Clear error when field is being edited
		if (errors[name as keyof FormData]) {
			setErrors((prev) => ({
				...prev,
				[name]: undefined,
			}));
		}
	};

	const validate = (): boolean => {
		const newErrors: Partial<FormData> = {};
		const emailError = validateEmail(formData.email);
		if (emailError) {
			newErrors.email = emailError;
		} 

		if (!formData.message) {
			newErrors.message = 'Message is required';
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (validate()) {
			onSubmit(formData);
		}
	};

	return (
		<div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50">
			<div className="min-h-screen w-full py-8 px-4 flex items-center justify-center">
				<div
					className="flex flex-col justify-center  bg-[#fff] shadow-custom p-4 w-[90%] max-w-[500px] border border-light-gray overflow-y-auto max-h-[90vh]"
					ref={modelref}
				>
					<div className="flex justify-between items-center mb-4">
						<h2 className="text-xl font-semibold text-gray-800">
							Send us a message
						</h2>
						<button
							onClick={onClose}
							className="text-gray-500 hover:text-gray-700"
							aria-label="Close"
						>
							<span className="text-2xl">&times;</span>
						</button>
					</div>

					<form onSubmit={handleSubmit}>
						<div className="mb-4">
							<label htmlFor="email" className="block text-gray-700 mb-1">
								Email <span className="text-red-500">*</span>
							</label>
							<input
								type="email"
								id="email"
								name="email"
								value={formData.email}
								onChange={handleChange}
								className={`w-full p-2 border rounded-md ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
							/>
							{errors.email && (
								<p className="text-red-500 text-sm mt-1">{errors.email}</p>
							)}
							<p className="text-gray-500  text-[10px] md:text-sm mt-1">
								We will only use your email if needed to respond to your message
								and won not share it with anyone else.
							</p>
						</div>

						<div className="mb-4">
							<label htmlFor="message" className="block text-gray-700 mb-1">
								Message <span className="text-red-500">*</span>
							</label>
							<div>
								<p className="text-gray-600 text-[10px] md:text-sm mb-2">
									If reporting a bug, please provide a detailed description,
									including:
								</p>
								<ol className="list-decimal ml-5 text-gray-600 text-[10px] md:text-sm mb-2">
									<li>The browser you were using.</li>
									<li>The url of the page that the bug occurred on.</li>
									<li className="font-medium">
										(important) The steps we would need to take to see the bug
										for ourselves.
									</li>
								</ol>
							</div>
							<textarea
								id="message"
								name="message"
								rows={4}
								value={formData.message}
								onChange={handleChange}
								className={`w-full p-2 border rounded-md ${errors.message ? 'border-red-500' : 'border-gray-300'}`}
							/>
							{errors.message && (
								<p className="text-red-500 text-sm mt-1">{errors.message}</p>
							)}
						</div>

						<button
							type="submit"
							className="w-full btn-dark text-white py-2 px-4 rounded-md transition duration-200"
						>
							Submit
						</button>
					</form>
				</div>
			</div>
		</div>
	);
};


